import { TSortMethod } from 'types/shared';
import { ICustomer } from 'redux/reducers/customers';
import { ICustomerOption } from '../shared/shared';

type TSelectedCustomerResult = (customerDisplayName: string) => ICustomerOption;

function createOptions(
  filteredCharts: any,
  customers: ICustomer[]
): TSelectedCustomerResult {
  return (customerDisplayName: string): ICustomerOption => {
    function countPositions(acc: number, chart: any): number {
      return acc + chart.territoryData
        ? chart.territoryData.length
        : chart.supportedCountryIds.length;
    }

    function filterByCustomer(chart: any): boolean {
      return chart.customerName === customerDisplayName;
    }

    const count = filteredCharts
      .filter(filterByCustomer)
      .reduce(countPositions, 0);

    const customer = customers.find(
      (cust: any) => customerDisplayName === cust.displayName
    ) as ICustomer;

    return {
      label:
        customerDisplayName === 'Billboard Charts'
          ? 'Billboard'
          : customerDisplayName,
      value: customer.name,
      positionsCount: count,
    };
  };
}

export function removeZeroPositionCounts(option: ICustomerOption): boolean {
  return option.positionsCount > 0;
}

/**
 * This sort function will first check if the given options contain a label that
 * exists in the given precedenceNames, if so these options will be sorted at the
 * top of the list, with the first name in the precendence list sorted on top.
 * Any other options will get sorted by position count descendingly.
 */
export function sortByPositionCountOrNamePrecedence(
  precedenceNames: string[]
): TSortMethod<ICustomerOption> {
  return (a: ICustomerOption, b: ICustomerOption): number => {
    const idA = a.label.toLowerCase();
    const idB = b.label.toLowerCase();

    const indexA = precedenceNames.indexOf(idA);
    const indexB = precedenceNames.indexOf(idB);

    if (indexA > indexB) {
      return -1;
    }

    if (indexA < indexB) {
      return 1;
    }

    return sortByPositionCount(a, b);
  };
}

function sortByPositionCount(a: ICustomerOption, b: ICustomerOption): number {
  return b.positionsCount - a.positionsCount;
}

interface IGetUniqueCustomerOptions {
  filteredCharts: any[] | null;
  customers: ICustomer[];
}
/**
 * Get unique customer options from filtered charts
 */
export const getUniqueCustomerOptions = ({
  filteredCharts,
  customers,
}: IGetUniqueCustomerOptions): any[] | null => {
  if (filteredCharts && customers) {
    const customerNames = filteredCharts.map(
      (chart: any) => chart.customerName
    );

    const uniqueCustomers = new Set(customerNames);

    if (uniqueCustomers.size === 0) {
      return null;
    }

    return Array.from<any>(uniqueCustomers)
      .map(createOptions(filteredCharts, customers))
      .filter(removeZeroPositionCounts)
      .sort(sortByPositionCountOrNamePrecedence(['apple music', 'spotify']));
  }

  return null;
};
